// src/client/hooks/use-settings.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiClient } from '@client/lib/api-client';
import { queryKeys } from '@client/lib/query-keys';
import type { Setting } from '@shared/types';
import { useCallback } from 'react';

// ============================================================================
// Main Settings Hook
// ============================================================================

export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings.lists(),
    queryFn: async () => {
      return await ApiClient.get<Setting[]>('/settings');
    },
  });
}

// ============================================================================
// Single Setting Hook
// ============================================================================

export function useSetting(key: string) {
  return useQuery({
    queryKey: queryKeys.settings.detail(key),
    queryFn: async () => {
      return await ApiClient.get<Setting>(`/settings/${key}`);
    },
  });
}

// ============================================================================
// Settings by Prefix Hook
// ============================================================================

export function useSettingsByPrefix(prefix: string) {
  return useQuery({
    queryKey: queryKeys.settings.byPrefix(prefix),
    queryFn: async () => {
      const allSettings = await ApiClient.get<Setting[]>('/settings');
      return allSettings.filter(s => s.key.startsWith(prefix));
    },
  });
}

// ============================================================================
// Mutation Hook for Updating Settings
// ============================================================================

export function useUpdateSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      return await ApiClient.put(`/settings/${key}`, { value });
    },
    onSuccess: (_, variables) => {
      // Invalidate all settings queries
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all });
      // Also invalidate specific setting
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.settings.detail(variables.key) 
      });
    },
  });
}

// ============================================================================
// Helper Hook: Get Setting Value with Default
// ============================================================================

export function useSettingValue(key: string, defaultValue: string = '') {
  const { data: settings = [] } = useSettings();
  const updateMutation = useUpdateSetting();

  const value = settings.find(s => s.key === key)?.value || defaultValue;

  const setValue = useCallback(async (newValue: string) => {
    await updateMutation.mutateAsync({ key, value: newValue });
  }, [key, updateMutation]);

  return {
    value,
    setValue,
    isLoading: updateMutation.isPending,
  };
}

// ============================================================================
// Specialized Hooks for Common Types
// ============================================================================

export function useBooleanSetting(key: string, defaultValue: boolean = false) {
  const { value, setValue, isLoading } = useSettingValue(
    key, 
    defaultValue.toString()
  );

  const booleanValue = value === 'true';

  const setBooleanValue = useCallback(async (newValue: boolean) => {
    await setValue(newValue.toString());
  }, [setValue]);

  return {
    value: booleanValue,
    setValue: setBooleanValue,
    isLoading,
  };
}

export function useNumberSetting(key: string, defaultValue: number = 0) {
  const { value, setValue, isLoading } = useSettingValue(
    key, 
    defaultValue.toString()
  );

  const numberValue = parseInt(value) || defaultValue;

  const setNumberValue = useCallback(async (newValue: number) => {
    await setValue(newValue.toString());
  }, [setValue]);

  return {
    value: numberValue,
    setValue: setNumberValue,
    isLoading,
  };
}

export function useJsonSetting<T>(key: string, defaultValue: T) {
  const { value, setValue, isLoading } = useSettingValue(
    key, 
    JSON.stringify(defaultValue)
  );

  let jsonValue: T;
  try {
    jsonValue = JSON.parse(value);
  } catch {
    jsonValue = defaultValue;
  }

  const setJsonValue = useCallback(async (newValue: T) => {
    await setValue(JSON.stringify(newValue));
  }, [setValue]);

  return {
    value: jsonValue,
    setValue: setJsonValue,
    isLoading,
  };
}

// ============================================================================
// Common Use Case Hooks
// ============================================================================

export function useAppInfo() {
  const { data: settings = [] } = useSettings();

  const getSetting = (key: string, defaultValue: string) => {
    return settings.find(s => s.key === key)?.value || defaultValue;
  };

  return {
    name: getSetting('app.name', 'TM Application'),
    version: getSetting('app.version', '2.0.0'),
    theme: getSetting('ui.theme', 'light'),
  };
}

export function useFeatureSettings(module: string) {
  const { data: settings = [] } = useSettings();
  const updateMutation = useUpdateSetting();

  const getFeatureSetting = useCallback((setting: string, defaultValue: string = '') => {
    const key = `features.${module}.${setting}`;
    return settings.find(s => s.key === key)?.value || defaultValue;
  }, [settings, module]);

  const setFeatureSetting = useCallback(async (setting: string, value: string) => {
    const key = `features.${module}.${setting}`;
    await updateMutation.mutateAsync({ key, value });
  }, [module, updateMutation]);

  const enabled = getFeatureSetting('enabled', 'true') === 'true';

  const setEnabled = useCallback(async (newEnabled: boolean) => {
    await setFeatureSetting('enabled', newEnabled.toString());
  }, [setFeatureSetting]);

  return {
    getSetting: getFeatureSetting,
    setSetting: setFeatureSetting,
    enabled,
    setEnabled,
    isLoading: updateMutation.isPending,
  };
}